import { Component, inject, signal, computed, effect, afterNextRender, DestroyRef, Injector, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { LayoutComponent } from './components/layout/layout.component';
import { AuthService } from './auth/services/auth.service';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionWarningDialogComponent } from './auth/components/session-warning-dialog/session-warning-dialog.component';
import { SessionNotificationsComponent } from './auth/components/session-notifications/session-notifications.component';
import { StartupLoadingOverlayComponent } from './components/startup-loading-overlay/startup-loading-overlay.component';
import { GlobalLoadingOverlayComponent } from './components/global-loading-overlay/global-loading-overlay.component';
import { TutorialWelcomeModalComponent } from './components/tutorial-welcome-modal/tutorial-welcome-modal.component';
import { TutorialService } from './services/tutorial.service';
import { ProgramDefinitionsService } from './services/program-definitions.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { UpdateBannerComponent } from './components/update-banner/update-banner.component';
import { Logger } from './shared/utils/logger.util';
import { ClickAnalyticsService, DynatraceService, DynatraceUserContextService } from './shared/dynatrace';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SideNavComponent, LayoutComponent, ToastModule, SessionWarningDialogComponent, SessionNotificationsComponent, StartupLoadingOverlayComponent, GlobalLoadingOverlayComponent, TutorialWelcomeModalComponent, ProgressSpinnerModule, UpdateBannerComponent],
  template: `
    @if (shouldShowSidebar()) {
      <div class="app-container">
        <app-side-nav></app-side-nav>
        <div class="main-content">
          <app-layout [hideChrome]="isIsolatedPortalRoute()">
            <router-outlet></router-outlet>
          </app-layout>
        </div>
      </div>
    } @else {
      <app-layout>
        <router-outlet></router-outlet>
      </app-layout>
    }

    <!-- Session Management Components -->
    <app-session-warning-dialog></app-session-warning-dialog>
    <app-session-notifications></app-session-notifications>

    <!-- Global Toast Messages -->
    <p-toast></p-toast>

    <!-- Global Loading Overlay (for HTTP requests) -->
    <app-global-loading-overlay></app-global-loading-overlay>

    <!-- Startup Loading Overlay -->
    <app-startup-loading-overlay></app-startup-loading-overlay>

    <!-- Tutorial Welcome Modal (for first-time users) -->
    <app-tutorial-welcome-modal></app-tutorial-welcome-modal>

    <!-- Tutorial Loading Overlay -->
    <div class="tutorial-loading-overlay" *ngIf="tutorialService.tutorialLoading()">
      <p-progressSpinner styleClass="w-4rem h-4rem" strokeWidth="4"></p-progressSpinner>
      <p>Loading...</p>
    </div>

    <!-- Version Update Banner -->
    @if (showUpdateBanner()) {
      <app-update-banner (refresh)="onRefreshApp()"></app-update-banner>
    }
  `,
  styles: [
    `
      .app-container {
        display: flex;
        min-height: 100vh;

        app-side-nav {
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 50;
          width: 280px; /* Fixed width for sidebar */
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          background-color: #f8fcfd;
          margin-left: 280px; /* Same as sidebar width */
          min-height: 100vh;
          overflow-y: auto; /* Allow main content to scroll */
        }
      }

      /* Mobile responsiveness */
      @media (max-width: 768px) {
        .app-container {
          app-side-nav {
            width: 200px; /* Smaller width on mobile */
          }

          .main-content {
            margin-left: 200px; /* Adjust for smaller sidebar */
          }
        }
      }

      @media (max-width: 480px) {
        .app-container {
          app-side-nav {
            width: 180px; /* Even smaller on very small screens */
          }

          .main-content {
            margin-left: 180px;
          }
        }
      }

      .tutorial-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(4px);
        z-index: 99999; /* Higher than driver.js popover */
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        
        p {
          margin-top: 1rem;
          color: #0b435b; /* Primary color */
          font-weight: 600;
          font-size: 1.1rem;
        }
      }
    `,
  ],
})
export class AppComponent {
  title = 'Bayshore ICS CX Hub';
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private swUpdate: SwUpdate | null = null;
  public tutorialService = inject(TutorialService); // Public for access in template
  private dynatrace = inject(DynatraceService);
  private clickAnalytics = inject(ClickAnalyticsService);
  /** Created here so it follows the signed-in user; it exposes nothing. */
  private readonly dynatraceUserContext = inject(DynatraceUserContextService);
  private programDefinitionsService = inject(ProgramDefinitionsService);
  private programDefinitionsLoaded = false;

  @ViewChild(TutorialWelcomeModalComponent) private tutorialWelcomeModal?: TutorialWelcomeModalComponent;

  private currentRoute = signal<string>('');
  private hasShownTutorial = signal(false);
  
  // Version update banner visibility
  showUpdateBanner = signal(false);

  // Polling interval for version checks (30 minutes)
  private readonly VERSION_CHECK_INTERVAL = 30 * 60 * 1000;
  // Initial delay before first version check – must be safely after the SW registration
  // window (registerWhenStable:30000 can wait up to 30s before the SW controls the page)
  private readonly INITIAL_CHECK_DELAY = 35 * 1000;
  // Minimum interval between navigation/visibility-triggered checks (5 minutes)
  private readonly NAV_CHECK_THROTTLE = 5 * 60 * 1000;
  private lastNavCheckTimestamp = 0;

  // Routes hosting an isolated portal (separate tenant, own auth/branding) that
  // must never render ICS Referral Hub chrome — sidebar, header, or footer —
  // even if a concurrent ICS session is authenticated in the same browser tab.
  // A client-side navigation into one of these routes does not reload the page,
  // so isAuthenticated() below would otherwise still reflect a live ICS session.
  private static readonly ISOLATED_PORTAL_ROUTE_PREFIXES = ['/ohah-portal'];

  readonly isIsolatedPortalRoute = computed(() =>
    AppComponent.ISOLATED_PORTAL_ROUTE_PREFIXES.some((prefix) => this.currentRoute().startsWith(prefix))
  );

  // Optimized signal for sidebar visibility using debounced route changes
  readonly shouldShowSidebar = computed(() => {
    const route = this.currentRoute();

    // Always hide sidebar for auth routes (login, invitation, etc.)
    if (route.startsWith('/auth')) {
      return false;
    }

    // Hide sidebar for standalone error/status pages that bypass normal app navigation
    if (route.startsWith('/hospital-inactive')) {
      return false;
    }

    if (this.isIsolatedPortalRoute()) {
      return false;
    }

    // Use store computed properties directly - these are already stable signals
    // Relaxed condition: show sidebar as long as user is authenticated to prevent layout collapse
    return this.authService.isAuthenticated();
  });

  constructor() {
    // Load the program-definition catalog once the user is authenticated.
    // No time-based cache: it is held for the session and refreshed on a full
    // app reload. Failure is non-fatal — the bundled-const fallback stays in
    // place until the next full app reload.
    effect(() => {
      if (this.authService.isAuthenticated() && !this.programDefinitionsLoaded) {
        this.programDefinitionsLoaded = true;
        this.programDefinitionsService.load().subscribe({
          error: () => {
            // Allow a retry if auth state later changes (e.g. token refresh).
            this.programDefinitionsLoaded = false;
          },
        });
      }
    });

    // Set initial route after render to avoid expression changed errors
    afterNextRender(() => {
      this.currentRoute.set(this.router.url);
      try {
        this.dynatrace.initialize();
        this.clickAnalytics.start();
      } catch {
        // Telemetry must never affect app startup
      }
      this.checkAndShowTutorial();
      this.initializeVersionChecks();
    });

    // Use afterNextRender for route updates to avoid timing issues
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.currentRoute.set(event.url);
        this.throttledVersionCheck();
        // Check here too — auth may not be ready when afterNextRender fires
        this.checkAndShowTutorial();
      });
  }

  /**
   * Check if we should show the tutorial welcome modal
   */
  private checkAndShowTutorial(): void {
    // Only show once per session
    if (this.hasShownTutorial()) {
      return;
    }

    // Never show on auth routes (login, invitation, password setup, etc.)
    if (this.currentRoute().startsWith('/auth')) {
      return;
    }

    // Check if user is authenticated
    if (!this.authService.isAuthenticated() || !this.authService.currentUser()) {
      return;
    }

    // Check if we should show tutorial (first-time user)
    if (this.tutorialService.shouldShowTutorial()) {
      this.hasShownTutorial.set(true);
      // Small delay to ensure all components are loaded
      setTimeout(() => {
        this.tutorialWelcomeModal?.show();
      }, 1000);
    }
  }

  /**
   * Initialize version update checks using Angular Service Worker.
   * This detects when a new version is deployed and shows an update banner.
   */
  private initializeVersionChecks(): void {
    this.swUpdate = this.resolveSwUpdate();

    if (!this.swUpdate) {
      Logger.warn('[Version Check] Service Worker update checks disabled due to DI failure');
      return;
    }

    const swUpdate = this.swUpdate;

    // Service Workers are disabled in dev mode
    if (!swUpdate.isEnabled) {
      Logger.debug('[Version Check] Service Worker not enabled (dev mode)');
      return;
    }

    // Subscribe to version update events
    swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        Logger.log('🆕 New version available:', event.latestVersion?.hash);
        this.showUpdateBanner.set(true);
      });

    // Handle unrecoverable SW state (e.g. corrupted cache after a deploy)
    swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        Logger.warn('[Version Check] Unrecoverable SW state:', event.reason);
        this.showUpdateBanner.set(true);
      });

    // Setup periodic polling (every 30 minutes)
    setInterval(() => {
      swUpdate.checkForUpdate().catch((err) => {
        Logger.warn('[Version Check] Failed to check for updates:', err);
      });
    }, this.VERSION_CHECK_INTERVAL);

    // Initial version check.
    // Returning users already have an active SW controlling the page, so a stale
    // client (e.g. one still targeting a retired API) can and must upgrade right
    // away — check IMMEDIATELY, with no first-paint cost since the SW is already
    // installed. First-time loads have no controller yet and nothing stale to fix,
    // so defer past the SW registration window to protect first paint.
    const runInitialCheck = () =>
      swUpdate.checkForUpdate().catch((err) => {
        Logger.warn('[Version Check] Initial check failed:', err);
      });

    const hasActiveController =
      typeof navigator !== 'undefined' &&
      !!navigator.serviceWorker &&
      !!navigator.serviceWorker.controller;

    if (hasActiveController) {
      Logger.debug('[Version Check] Active SW controller present — checking immediately');
      runInitialCheck();
    } else {
      setTimeout(runInitialCheck, this.INITIAL_CHECK_DELAY);
    }

    // Check when the browser tab regains visibility (user returns after being away)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.throttledVersionCheck();
      }
    });
  }

  /**
   * Trigger a version check at most once per NAV_CHECK_THROTTLE interval.
   * Called on route navigation and tab visibility change.
   */
  private throttledVersionCheck(): void {
    if (!this.swUpdate || !this.swUpdate.isEnabled || this.showUpdateBanner()) {
      return;
    }
    const now = Date.now();
    if (now - this.lastNavCheckTimestamp < this.NAV_CHECK_THROTTLE) {
      return;
    }
    this.lastNavCheckTimestamp = now;
    Logger.debug('[Version Check] Throttled check triggered');
    this.swUpdate.checkForUpdate().catch((err) => {
      Logger.warn('[Version Check] Throttled check failed:', err);
    });
  }

  /**
   * Called when user clicks the refresh button on the update banner.
   * Triggers a full page reload to activate the new version.
   */
  public onRefreshApp(): void {
    // Track that the reload was triggered by the update banner
    sessionStorage.setItem('RELOAD_TRIGGERED_BY', 'update-banner');
    // Activate the new service worker version first, then reload.
    // Without this, the old SW may still serve stale/mixed assets on reload,
    // which can break auth restoration and redirect users to the login screen.
    if (this.swUpdate?.isEnabled) {
      this.swUpdate.activateUpdate().then(() => {
        window.location.reload();
      }).catch(() => {
        // Fallback: reload anyway if activation fails
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  }

  private resolveSwUpdate(): SwUpdate | null {
    return this.injector.get(SwUpdate, null);
  }
}
